import React, { useEffect, useMemo } from "react";
import { Button, Checkbox, Input, List, message, Select, Space, Switch, Typography } from "antd";
import keyBy from "lodash.keyby";
import { useTranslation } from "react-i18next";

import { teamActions, traineesActions } from "@fitness-app/app-store";
import { PermissionKey } from "@fitness-app/data-models";
import { getErrorMessage } from "@fitness-app/utils";

import { useTagOptions } from "~/hooks/useTagOptions";
import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { type SelectedUsersListItem } from "~/modules/Chat/SendMessageToMultipleUsers/types";
import { useAppDispatch } from "~/store/initializeStore";

interface SelectedUsersListProps {
  selectedUsers: SelectedUsersListItem[];
  setSelectedUsers: (users: SelectedUsersListItem[]) => void;
  setSelectedTags: (tags: string[]) => void;
  selectedTags: string[];
  condition: "hasTag" | "notHasTag" | false;
  changeCondition: (value: "hasTag" | "notHasTag" | false) => void;
  sendToMarketingChannelIfExists: boolean;
  toggleSendToMarketingChannel: (value: boolean) => void;
  hideMarketingChannelOption?: boolean;
}

const { Search } = Input;

/** Sentinel for the trainer filter — clients with no assigned trainer. */
const NO_TRAINER = "__none__";

export const SelectedUsersList = ({
  selectedUsers,
  setSelectedUsers,
  setSelectedTags,
  changeCondition,
  condition,
  sendToMarketingChannelIfExists,
  toggleSendToMarketingChannel,
  hideMarketingChannelOption,
}: SelectedUsersListProps): React.ReactElement => {
  const { options: tags } = useTagOptions("traineeTags");
  const dispatch = useAppDispatch();
  const { t } = useTranslation("dashboard");
  const { memberId } = useUserClaims();
  const [clientList, setClientList] = React.useState<SelectedUsersListItem[]>([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [productFilter, setProductFilter] = React.useState<string | null>(null);
  const [trainerFilter, setTrainerFilter] = React.useState<string | null>(null);
  // Full access by default — limited clients are reachable on purpose (announcement
  // channel targeting, TRE-801), but only behind an explicit filter change.
  const [accessFilter, setAccessFilter] = React.useState<"fullAccess" | "limited" | "all">("fullAccess");

  const clientsById = useMemo(() => keyBy(clientList, "id"), [clientList]);

  useEffect(() => {
    void dispatch(teamActions.fetchTrainerConfig());
    // Scoped by `chat.write`, the key SendMessageToMultipleUsersUseCase authorizes on — not by
    // `clients.read`. A head coach can read the whole tenant but message only her team, and an
    // explicit recipient outside that scope makes the backend reject the WHOLE send (TRE-2148).
    dispatch(traineesActions.fetchAllActiveTrainees({ includeLimited: true, scopeKey: PermissionKey.CHAT_WRITE }))
      .unwrap()
      .then((response) => {
        setClientList(response.list as SelectedUsersListItem[]);
      })
      .catch((e) => message.error(getErrorMessage(e)));
  }, [dispatch]);

  const shouldHideList = Boolean(condition);

  const selectedById = useMemo(() => keyBy(selectedUsers, "id"), [selectedUsers]);

  const onItemChange = (checked: boolean, id: string) => {
    if (checked && !selectedById[id]) {
      const selected = clientsById[id];
      setSelectedUsers(selected ? [...selectedUsers, selected] : selectedUsers);
    } else if (!checked && selectedById[id]) {
      setSelectedUsers(selectedUsers.filter((item) => item.id !== id));
    }
  };

  const productOptions = useMemo(() => {
    const byId = new Map<string, string>();
    clientList.forEach((client) => {
      const product = client.productClient?.product;
      if (product) {
        byId.set(product.id, product.name);
      }
    });
    return [...byId.entries()].map(([value, label]) => ({ value, label }));
  }, [clientList]);

  // A head coach with tenant-wide `chat.write` sees every client in the tenant and had no way
  // to narrow the list down to her own athletes (TRE-2173). Options come from the fetched list,
  // so they mirror exactly what is pickable; the caller's own entry is pinned first.
  const trainerOptions = useMemo(() => {
    const byId = new Map<string, string>();
    let hasUnassigned = false;
    clientList.forEach((client) => {
      const trainer = client.assignedTrainer;
      if (client.assignedTrainerId && trainer) {
        byId.set(client.assignedTrainerId, `${trainer.firstName} ${trainer.lastName}`.trim());
      } else if (!client.assignedTrainerId) {
        hasUnassigned = true;
      }
    });
    const options = [...byId.entries()]
      .filter(([value]) => value !== memberId)
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pl"));
    if (memberId && byId.has(memberId)) {
      options.unshift({ value: memberId, label: "Moi podopieczni" });
    }
    if (hasUnassigned) {
      options.push({ value: NO_TRAINER, label: "Bez przypisanego trenera" });
    }
    return options;
  }, [clientList, memberId]);

  const filteredClients = useMemo(() => {
    return clientList.filter((client) => {
      if (accessFilter !== "all" && client.type !== accessFilter) {
        return false;
      }
      if (productFilter && client.productClient?.product?.id !== productFilter) {
        return false;
      }
      if (trainerFilter === NO_TRAINER && client.assignedTrainerId) {
        return false;
      }
      if (trainerFilter && trainerFilter !== NO_TRAINER && client.assignedTrainerId !== trainerFilter) {
        return false;
      }
      if (searchTerm.length > 0) {
        return (
          client.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.lastName.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      return true;
    });
  }, [clientList, searchTerm, productFilter, accessFilter, trainerFilter]);

  // Derived from the visible cohort, not stored: a checkbox with its own state stayed checked
  // after switching trainer/product while the new view had nothing selected (PR #1800 review).
  const visibleSelectedCount = useMemo(
    () => filteredClients.filter((client) => selectedById[client.id]).length,
    [filteredClients, selectedById],
  );
  const allVisibleSelected = filteredClients.length > 0 && visibleSelectedCount === filteredClients.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  return (
    <div>
      <Space direction="vertical" size={16}>
        {/* No scope gate here — the modal already requires chat.write, and the backend narrows the
            recipients to the caller's scope; any author may post to the announcement channel (TRE-801). */}
        {!hideMarketingChannelOption && (
          <Space>
            <Switch checked={sendToMarketingChannelIfExists} onChange={toggleSendToMarketingChannel} />
            <Typography.Text>Wyślij wiadomości na kanał ogłoszeniowy/marketingowy jeśli istnieje</Typography.Text>
          </Space>
        )}
        <Space>
          <Switch
            checked={condition === "hasTag"}
            onChange={(active) => {
              changeCondition(active ? "hasTag" : false);
              setSelectedTags([]);
            }}
          />
          <Typography.Text>Wyślij do wszystkich klientów którzy posiadają tag:</Typography.Text>
        </Space>
        {condition === "hasTag" && (
          <Select
            placeholder="Wybierz tagi..."
            style={{ minWidth: 250 }}
            options={tags}
            mode="multiple"
            maxTagCount={10}
            onChange={setSelectedTags}
          />
        )}
        <Space>
          <Switch
            checked={condition === "notHasTag"}
            onChange={(active) => {
              changeCondition(active ? "notHasTag" : false);
              setSelectedTags([]);
            }}
          />
          <Typography.Text>Wyślij do wszystkich klientów którzy nie posiadają tag:</Typography.Text>
        </Space>

        {condition === "notHasTag" && (
          <Select
            placeholder="Wybierz tagi..."
            style={{ minWidth: 250 }}
            mode="multiple"
            maxTagCount={10}
            options={tags}
            onChange={setSelectedTags}
          />
        )}

        {shouldHideList ? null : (
          <Checkbox
            indeterminate={someVisibleSelected}
            onChange={(value) => {
              // Operates only on what the current filters show — checking must not select
              // the whole roster, and unchecking must not drop clients picked under a
              // previous filter.
              const visibleIds = new Set(filteredClients.map((client) => client.id));
              setSelectedUsers(
                value.target.checked
                  ? [...selectedUsers.filter((client) => !visibleIds.has(client.id)), ...filteredClients]
                  : selectedUsers.filter((client) => !visibleIds.has(client.id)),
              );
            }}
            checked={allVisibleSelected}
          >
            Zaznacz {productFilter || trainerFilter || searchTerm ? "przefiltrowanych" : "wszystkich"} klientów (
            {selectedUsers.length})
          </Checkbox>
        )}
      </Space>

      {!shouldHideList && (
        <div className="mt-4 flex flex-col gap-y-2">
          <Space size={4} wrap>
            <Search
              value={searchTerm}
              placeholder="Wyszkuj klienta..."
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 250 }}
            />
            <Select
              placeholder="Produkt"
              allowClear
              value={productFilter}
              onChange={(value) => setProductFilter(value ?? null)}
              options={productOptions}
              // Fixed width — a selected long product name must not resize the row and
              // push the inputs apart.
              style={{ width: 220 }}
            />
            <Select
              placeholder="Trener"
              allowClear
              showSearch
              optionFilterProp="label"
              value={trainerFilter}
              onChange={(value) => setTrainerFilter(value ?? null)}
              options={trainerOptions}
              style={{ width: 220 }}
            />
            <Select
              value={accessFilter}
              onChange={setAccessFilter}
              options={[
                { value: "fullAccess", label: "Pełny dostęp" },
                { value: "limited", label: "Ograniczony dostęp" },
                { value: "all", label: "Wszystkie dostępy" },
              ]}
              style={{ width: 170 }}
            />
            {Boolean(searchTerm) && (
              <Button onClick={() => setSearchTerm("")} type="link">
                {t("products:search.reset")}
              </Button>
            )}
          </Space>
          <Checkbox.Group value={selectedUsers.map((item) => item.id)}>
            <List
              itemLayout="horizontal"
              split={false}
              pagination={{
                defaultCurrent: 1,
                defaultPageSize: 20,
                hideOnSinglePage: true,
                showSizeChanger: true,
                pageSizeOptions: [20, 50, 100],
                showTotal: (total) => `${total} klientów`,
              }}
              dataSource={filteredClients}
              renderItem={(item) => (
                <List.Item>
                  <Checkbox value={item.id} onChange={(event) => onItemChange(event.target.checked, item.id)}>
                    {item.firstName} {item.lastName}
                    {item.productClient?.product?.name ? (
                      <Typography.Text type="secondary" className="ml-2 text-xs">
                        {item.productClient.product.name}
                      </Typography.Text>
                    ) : null}
                    {item.type === "limited" ? (
                      <Typography.Text type="secondary" className="ml-2 text-xs">
                        (ograniczony dostęp)
                      </Typography.Text>
                    ) : null}
                  </Checkbox>
                </List.Item>
              )}
              locale={{ emptyText: "Brak aktywnych klientów" }}
            />
          </Checkbox.Group>
        </div>
      )}
    </div>
  );
};
